import { Check, Divide } from "lucide-react";

import SeletorQuantidade from "./SeletorQuantidade";
import { formatarQtd } from "../../../lib/unidadeVenda";
import { unidadesRestantes, valorItem } from "../lib/divisao";
import { brl } from "../lib/formato";

function Foto({ item, getImageUrl, esmaecida }) {
  return (
    <div className={`w-10 h-10 md:w-12 md:h-12 bg-muted rounded-lg shrink-0 overflow-hidden ${esmaecida ? "opacity-70" : ""}`}>
      {item.photo_url
        ? <img src={getImageUrl(item.photo_url)} alt="" className="w-full h-full object-cover" />
        : <div className="w-full h-full flex items-center justify-center text-lg">🍽️</div>}
    </div>
  );
}

function Marcador({ pago, selecionado, travado }) {
  if (pago) {
    return <div className="w-5 h-5 mt-0.5 rounded-full bg-green-500 flex items-center justify-center shrink-0"><Check className="w-3 h-3 text-white" /></div>;
  }
  if (travado) {
    return <div className="w-5 h-5 mt-0.5 rounded bg-amber-500/20 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0"><Divide className="w-3 h-3" /></div>;
  }
  return (
    <div className={`w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center shrink-0 ${selecionado ? "bg-primary border-primary" : "border-muted-foreground/40"}`}>
      {selecionado && <Check className="w-3 h-3 text-primary-foreground" />}
    </div>
  );
}

// Barra + "Pago R$ X de R$ Y" + uma etiqueta por pagamento que caiu neste produto
function ProgressoPagamento({ item, total }) {
  const pago = item._paid ? total : Number(item._pagoParcial) || 0;
  const hist = item._histParcial || [];
  if (!item._paid && pago <= 0) return null;
  const pct = total > 0 ? Math.min(100, (pago / total) * 100) : 100;
  return (
    <div className="mt-1.5 space-y-1">
      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
        <div className={`h-full rounded-full ${item._paid ? "bg-green-500" : "bg-amber-500"}`} style={{ width: `${pct}%` }} />
      </div>
      {!item._paid && (
        <p className="text-[11px] tabular-nums">
          <span className="font-semibold text-green-600 dark:text-green-400">Pago {brl(pago)}</span>
          <span className="text-muted-foreground"> de {brl(total)} · </span>
          <span className="font-semibold text-orange-600 dark:text-orange-400">falta {brl(Math.max(0, total - pago))}</span>
        </p>
      )}
      {hist.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {hist.map((h, i) => (
            <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/10 text-green-700 dark:text-green-400 tabular-nums">
              {h.label} {brl(h.valor)}{h.parte ? ` · ${h.parte}` : ""}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Quilo/litro (30/09/2026): "1,250 kg × R$ 59,90/kg" em vez de "1.25× R$ 59,90"
const qtdPreco = (item, unitPrice, medida) => (medida
  ? `${formatarQtd(item.quantity || 1, medida)} × ${brl(unitPrice)}/${medida}`
  : `${item.quantity || 1}× ${brl(unitPrice)}`);

function Detalhes({ item, pago, naDivisao, medida }) {
  const unitPrice = item.final_price ?? item.sale_price ?? 0;
  const obs = item.observation || item.observacao;
  const subs = (item.subitems || []).map((s) => s.nome || s.name).filter(Boolean);
  return (
    <>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <p className={`font-semibold text-sm truncate ${pago ? "text-green-700 dark:text-green-400" : ""}`}>{item.name || item.nome}</p>
          {item.combo_type && (
            <span className={`text-[9px] px-1 py-0.5 rounded shrink-0 ${item.combo_type === "combo" ? "bg-green-500/20 text-green-700 dark:text-green-400" : "bg-blue-500/20 text-blue-600 dark:text-blue-400"}`}>
              {item.combo_type === "combo" ? "COMBO" : "SIMPLES"}
            </span>
          )}
        </div>
        <span className={`font-bold text-sm shrink-0 tabular-nums ${pago ? "text-green-600" : ""}`}>{brl(valorItem(item))}</span>
      </div>
      {obs && <p className="text-xs text-orange-500 mt-0.5 line-clamp-2">📝 {obs}</p>}
      {subs.length > 0 && <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2">{subs.join(" • ")}</p>}
      <div className="flex items-center gap-2 mt-0.5">
        <p className="text-xs font-bold text-primary tabular-nums">{qtdPreco(item, unitPrice, medida)}</p>
        {pago && item._paymentInfo && <p className="text-xs font-medium text-green-600 truncate">{item._paymentInfo}</p>}
        {naDivisao && !pago && <p className="text-[11px] font-semibold text-amber-600 dark:text-amber-400">Na divisão</p>}
      </div>
    </>
  );
}

// `rodape`: o que a tela quiser embaixo do produto (no Fechar mesa: de quem é
// e quem lançou). `medida` ("kg" | "L" | null, pelo cadastro): peso sai
// "1,250 kg × R$ 59,90/kg" e é pago inteiro, sem o "Pagar quantas?".
export default function ProdutoPagamentoCard({ item, onToggle, onQtd, getImageUrl, naDivisao, rodape, medida = null }) {
  const pago = !!item._paid;
  const clicavel = !pago && !naDivisao;
  const restantes = medida ? Math.min(1, unidadesRestantes(item)) : unidadesRestantes(item);

  let visual = "border-transparent bg-muted/30 hover:bg-muted/60 cursor-pointer";
  if (pago) visual = "bg-green-50 dark:bg-green-900/20 border-green-500/40";
  else if (naDivisao) visual = "border-amber-500/40 bg-amber-500/5";
  else if (item._selected) visual = "border-primary bg-primary/5 cursor-pointer";

  // div e não button: o seletor de quantidade tem botões dentro
  return (
    <div
      role="button"
      tabIndex={clicavel ? 0 : -1}
      aria-pressed={!!item._selected}
      aria-disabled={!clicavel}
      onClick={clicavel ? onToggle : undefined}
      onKeyDown={(e) => { if (clicavel && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onToggle(); } }}
      className={`w-full flex items-start gap-2.5 p-2.5 md:p-3 rounded-lg border-2 transition-all text-left ${visual}`}
    >
      <Marcador pago={pago} selecionado={item._selected} travado={naDivisao} />
      <Foto item={item} getImageUrl={getImageUrl} esmaecida={pago} />
      <div className="flex-1 min-w-0">
        <Detalhes item={item} pago={pago} naDivisao={naDivisao} medida={medida} />
        {rodape}
        <ProgressoPagamento item={item} total={valorItem(item)} />
        {clicavel && item._selected && restantes > 1 && (
          <SeletorQuantidade
            restantes={restantes}
            qtd={item._selQtd}
            precoUnit={item.final_price ?? item.sale_price ?? 0}
            onEscolher={onQtd}
          />
        )}
      </div>
    </div>
  );
}
